import { useState } from 'react';
import type { PlaceValueGroup } from './place-value-api';
import type { MapPlaceReading } from './vsj-map-place-api';

// ── Label maps ─────────────────────────────────────────────────────────────────

const PT_LABELS: Record<string, string> = {
  house: 'Casas',
  apartment: 'Departamentos',
  lot: 'Lotes',
  commercial: 'Locales comerciales',
  warehouse: 'Depósitos',
  other: 'Inmuebles',
};

const OP_LABELS: Record<string, string> = {
  sale: 'en venta',
  rent: 'en alquiler',
};

const CONFIDENCE_CLASSES: Record<string, string> = {
  high: 'vsj-pvc-confidence--high',
  medium: 'vsj-pvc-confidence--medium',
  low: 'vsj-pvc-confidence--low',
};

const CONFIDENCE_RANK: Record<string, number> = { high: 3, medium: 2, low: 1 };

// ── Shared helpers ─────────────────────────────────────────────────────────────

function fmt(n: number | null): string {
  if (n == null) return '—';
  return n.toLocaleString('es-AR', { maximumFractionDigits: 0 });
}

function groupTitle(g: PlaceValueGroup): string {
  const pt = PT_LABELS[g.propertyType] ?? g.propertyType;
  const op = OP_LABELS[g.operation] ?? g.operation;
  return `${pt} ${op} · ${g.currency}`;
}

/** Returns the group with highest sampleCount; breaks ties by confidence. */
export function selectPrimaryGroup(groups: PlaceValueGroup[]): PlaceValueGroup | null {
  if (groups.length === 0) return null;
  return (
    [...groups].sort((a, b) => {
      if (b.sampleCount !== a.sampleCount) return b.sampleCount - a.sampleCount;
      return (CONFIDENCE_RANK[b.confidenceLevel] ?? 0) - (CONFIDENCE_RANK[a.confidenceLevel] ?? 0);
    })[0] ?? null
  );
}

/**
 * Builds "28 referencias de casas y departamentos en los últimos meses."
 * Returns null when data is insufficient to form a meaningful sentence.
 */
export function buildContextSentence(reading: MapPlaceReading): string | null {
  const { observationCount: count, observedTypes: types, periodLabel: period } = reading;
  if (!count || count === 0 || !types || types.length === 0) return null;

  let typesStr: string;
  if (types.length === 1) {
    typesStr = types[0] ?? '';
  } else if (types.length === 2) {
    typesStr = `${types[0]} y ${types[1]}`;
  } else {
    typesStr = `${types.slice(0, -1).join(', ')} y ${types[types.length - 1]}`;
  }

  const countStr = count.toLocaleString('es-AR');
  return `${countStr} referencias de ${typesStr}${period ? ` ${period}` : ''}.`;
}

// ── Header — the place, not the price ──────────────────────────────────────────

interface PlaceCardHeaderProps {
  reading: MapPlaceReading | null;
  onClose: () => void;
}

export function PlaceCardHeader({ reading, onClose }: PlaceCardHeaderProps): JSX.Element {
  const placeName = reading?.streetName ?? 'Lugar seleccionado en el mapa';
  const placeSubtitle = reading?.streetTypeLabel
    ? `${reading.streetTypeLabel} · San Juan`
    : 'San Juan';

  return (
    <div className="vsj-pvc-header">
      <div>
        <div className="vsj-pvc-overline">Ficha del lugar</div>
        <h3 className="vsj-pvc-title">{placeName}</h3>
        <div className="vsj-pvc-scope">{placeSubtitle}</div>
      </div>
      <button type="button" className="vsj-pvc-close" onClick={onClose} aria-label="Cerrar ficha">
        ×
      </button>
    </div>
  );
}

// ── Entorno ────────────────────────────────────────────────────────────────────

export function PlaceCardContext({ sentence }: { sentence: string }): JSX.Element {
  return (
    <div className="vsj-pvc-section">
      <div className="vsj-pvc-section-label">Entorno</div>
      <p className="vsj-pvc-context-sentence">{sentence}</p>
    </div>
  );
}

// ── Referencia de valor ────────────────────────────────────────────────────────

function GroupRow({ g }: { g: PlaceValueGroup }): JSX.Element {
  const { priceRange: pr, pricePerM2Range: ppm2 } = g;
  return (
    <div className="vsj-pvc-group">
      <div className="vsj-pvc-group-title">{groupTitle(g)}</div>
      <div className="vsj-pvc-group-refs">{g.sampleCount} referencias</div>
      <div className="vsj-pvc-price-range">
        {g.currency} {fmt(pr.p25)} – {fmt(pr.p75)}
      </div>
      <div className="vsj-pvc-price-median">
        Mediana: {g.currency} {fmt(pr.median)}
      </div>
      {ppm2 && (
        <div className="vsj-pvc-ppm2">
          {g.currency} {fmt(ppm2.p25)} – {fmt(ppm2.p75)} /m²
          <br />
          Mediana: {g.currency} {fmt(ppm2.median)} /m²
        </div>
      )}
      <span className={`vsj-pvc-confidence ${CONFIDENCE_CLASSES[g.confidenceLevel] ?? ''}`}>
        {g.confidenceLabel}
      </span>
    </div>
  );
}

interface PlaceCardValueSummaryProps {
  primary: PlaceValueGroup;
  others: PlaceValueGroup[];
}

export function PlaceCardValueSummary({
  primary,
  others,
}: PlaceCardValueSummaryProps): JSX.Element {
  const [showOthers, setShowOthers] = useState(false);
  const { priceRange: pr } = primary;

  return (
    <div className="vsj-pvc-section vsj-pvc-section--value">
      <div className="vsj-pvc-section-label">Referencia de valor</div>

      <div className="vsj-pvc-primary-group">
        <div className="vsj-pvc-primary-group-type">{groupTitle(primary)}</div>
        <div className="vsj-pvc-primary-range">
          {primary.currency} {fmt(pr.p25)} — {fmt(pr.p75)}
        </div>
        <div className="vsj-pvc-primary-refs">
          Rango orientativo · {primary.sampleCount} referencias
        </div>
        <span className={`vsj-pvc-confidence ${CONFIDENCE_CLASSES[primary.confidenceLevel] ?? ''}`}>
          {primary.confidenceLabel}
        </span>
      </div>

      {others.length > 0 && !showOthers && (
        <button type="button" className="vsj-pvc-show-more" onClick={() => setShowOthers(true)}>
          Ver otros tipos ({others.length} tipo{others.length !== 1 ? 's' : ''} más)
        </button>
      )}

      {showOthers &&
        others.map((g, i) => (
          <GroupRow key={`${g.operation}-${g.propertyType}-${g.currency}-${i}`} g={g} />
        ))}
    </div>
  );
}

// ── Sin referencias suficientes ────────────────────────────────────────────────

export function PlaceCardUnavailable(): JSX.Element {
  return (
    <div className="vsj-pvc-section vsj-pvc-section--value">
      <div className="vsj-pvc-section-label">Referencia de valor</div>
      <p className="vsj-pvc-unavailable-note">
        Aún no tenemos referencias activas suficientes en este entorno.
      </p>
      <p className="vsj-pvc-unavailable-hint">Podés hacer una consulta guiada igual.</p>
    </div>
  );
}
